import { Box, makeStyles } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import { localizationTable, options } from 'constants/table';
import MaterialTable from 'material-table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CertificateData } from 'services/dataService';
import FileSaver from 'file-saver';
import { format, parseISO, isValid } from 'date-fns';

const formatWorkload = (workload) => {
  const totalHours = Number(workload || 0);

  if (!Number.isFinite(totalHours) || totalHours <= 0) {
    return '0:00';
  }

  const totalMinutes = Math.round(totalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const TrailCertificateTable = ({ id_user }) => {
  const classes = useStyles();
  const { t } = useTranslation(['tables', 'button', 'toastify']);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [secondPageCourses, setSecondPageCourses] = useState([]);

  const getData = useCallback(async () => {
    if (!id_user) return;

    setLoading(true);
    try {
      const response = await CertificateData.getStudentFinishedTrails(id_user);
      setData(response?.data?.results || []);
    } catch (error) {
      console.error('Erro ao buscar certificados de trilha:', error);
    }
    setLoading(false);
  }, [id_user]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleDownloadCertificate = useCallback(
    async (rowData) => {
      try {
        toast.success(t('toastify:commons.toast_success_certificate'));

        setSecondPageCourses(rowData?.courses || []);

        await new Promise((resolve) => setTimeout(resolve, 0));

        const secondPageCanvas = await html2canvas(document.getElementById('trail-certificate-second-page'), {
          scale: 2,
          useCORS: true,
        });
        const secondPageImage = secondPageCanvas.toDataURL('image/png');

        const requestBody = {
          id_user,
          certificate_create_date: rowData?.certificateCreation,
          secondPageImage,
        };

        const { data: base64CertificateFile } = await CertificateData.downloadTrail(
          rowData?.id_trail,
          requestBody
        );

        const responsePDF = await fetch(base64CertificateFile);
        const blob = await responsePDF.blob();
        FileSaver.saveAs(blob, `${rowData?.name}.pdf`);
      } catch (error) {
        console.error('Erro ao gerar o certificado de trilha:', error);
      }
    },
    [id_user, t]
  );

  return (
    <>
      <div
        id="trail-certificate-second-page"
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          width: '1366px',
          height: '768px',
          padding: '25px',
          boxSizing: 'border-box',
          background: '#fff',
          fontFamily: 'Arial, sans-serif',
          fontSize: '16px',
          color: '#000',
        }}
      >
        <h2 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '30px' }}>
          Conteúdo Programático
        </h2>

        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {secondPageCourses.map((course, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '16px',
                lineHeight: '2.2',
                borderBottom: '1px solid #ccc',
                padding: '4px 0',
              }}
            >
              <span>{course?.name}</span>
              <span>{formatWorkload(course?.workload)}</span>
            </div>
          ))}
        </div>
      </div>
      <Box className={classes.root}>
        <MaterialTable
          isLoading={loading}
          title={`${t('tables:Certificate.title')}`}
          columns={[
            {
              title: t('tables:Certificate.name_column'),
              field: 'name',
              render: (rowData) => (
                <p
                  style={{ color: '#176AE6', cursor: 'pointer' }}
                  onClick={() => handleDownloadCertificate(rowData)}
                >
                  {rowData?.name}
                </p>
              ),
            },
            {
              title: t('tables:Certificate.workload_column'),
              field: 'workload',
              render: (rowData) => <span>{formatWorkload(rowData?.workload)}</span>,
            },
            {
              title: t('tables:Certificate.date_emission_column'),
              field: 'certificateCreation',
              type: 'date',
              render: (rowData) => {
                const certificateDate = rowData?.certificateCreation
                  ? parseISO(rowData.certificateCreation)
                  : null;

                return certificateDate && isValid(certificateDate)
                  ? format(certificateDate, 'dd/MM/yyyy - HH:mm')
                  : 'N/A';
              },
            },
          ]}
          data={data}
          components={{ search: false }}
          localization={localizationTable(t, {
            emptyDataSourceMessage: 'tables:Certificate.empty_data',
            actions: 'button:commons.btn_download',
          })}
          options={{ ...options, emptyRowsWhenPaging: false, search: false }}
          actions={[
            {
              icon: () => <GetApp style={{ color: '#176AE6' }} />,
              tooltip: 'Baixar certificado',
              onClick: (_, rowData) => handleDownloadCertificate(rowData),
            },
          ]}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
}));

export default React.memo(TrailCertificateTable);
